import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Share2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Pagination } from '@/shared/components/custom/pagination/Pagination';
import { usePaginationFromUrl } from '@/shared/hooks/usePaginationFromUrl';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { useForm } from '../hooks/useForm';
import { useCreateForm, useUpdateForm } from '../hooks/useFormMutations';
import { useSubmissions } from '../hooks/useSubmissions';
import { FormStatusBadge } from '../components/FormsList/FormStatusBadge';
import { FormSettingsPopover } from '../components/FormSettingsPopover';
import { ShareFormDialog } from '../components/ShareFormDialog';
import { FormFieldsEditor } from '../components/FormBuilder/FormFieldsEditor';
import { FormPreviewPane } from '../components/FormBuilder/FormPreviewPane';
import { FormAnalyticsDashboard } from '../components/FormSubmissions/FormAnalyticsDashboard';
import { SubmissionsTable } from '../components/FormSubmissions/SubmissionsTable';
import { toFieldInputPayload, fromFormDetailField } from '../adapters/field-type.adapter';
import type { BuilderField } from '../components/FormBuilder/FormFieldEditorRow';
import type { CreateFormPayload, FormDetail } from '../types';

export default function FormEditorPage() {
  const { t } = useTranslation('forms');
  const navigate = useNavigate();
  const { orgId, formId } = useParams<{ orgId: string; formId: string }>();
  const [searchParams] = useSearchParams();
  const isNew = formId === 'new';
  const initialTab = searchParams.get('tab') === 'submissions' && !isNew ? 'submissions' : 'editor';

  const { form, isLoading } = useForm(isNew ? '' : (formId ?? ''));
  const createForm = useCreateForm();
  const updateForm = useUpdateForm(isNew ? '' : (formId ?? ''));
  const [shareOpen, setShareOpen] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [builderFields, setBuilderFields] = useState<BuilderField[]>([]);
  const [emailRequired, setEmailRequired] = useState(false);
  const [projectId, setProjectId] = useState<string | undefined>(undefined);

  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const pagination = usePaginationFromUrl({ totalItems: totalSubmissions });
  const {
    submissions,
    pageInfo,
    isLoading: submissionsLoading,
  } = useSubmissions(
    isNew ? '' : (formId ?? ''),
    {},
    pagination.pageIndex + 1,
    pagination.pageSize,
  );

  if (pageInfo && pageInfo.totalCount !== totalSubmissions) {
    setTotalSubmissions(pageInfo.totalCount);
  }

  // Seeded to `null` (never a valid form), not `form` — a warm query cache can already hold
  // `form` synchronously on the very first render of a fresh mount, which would make an
  // initializer of `form` equal itself immediately and silently skip this sync forever.
  const [syncedForm, setSyncedForm] = useState<FormDetail | null>(null);
  if (form && form !== syncedForm) {
    setSyncedForm(form);
    setTitle(form.title);
    setDescription(form.description ?? '');
    setEmailRequired(form.emailRequired);
    setProjectId(form.projectId ?? undefined);
    setBuilderFields(
      form.fields.map((f) => ({ ...fromFormDetailField(f), localId: f.id ?? crypto.randomUUID() })),
    );
  }

  if (!isNew && (isLoading || !form)) {
    return (
      <div className="p-4 md:p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const isDraft = isNew || form?.status === 'Draft';

  function buildPayload(): CreateFormPayload {
    return {
      title,
      description: description || undefined,
      projectId,
      emailRequired,
      fields: builderFields
        .slice()
        .sort((a, b) => a.order - b.order)
        .map(toFieldInputPayload),
    };
  }

  async function handleSave() {
    try {
      if (isNew) {
        const { data: created } = await createForm.mutateAsync(buildPayload());
        navigate(`/${orgId}/forms/${created.id}`, { replace: true });
        return;
      }
      await updateForm.mutateAsync(buildPayload());
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  const isSaving = createForm.isPending || updateForm.isPending;

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <div className="p-4 md:p-6 space-y-4" data-testid="forms-editor-page">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/${orgId}/forms`)}
              aria-label={t('list.title')}
              data-testid="forms-editor-back-button"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1
              className="text-lg font-semibold truncate max-w-[240px] md:max-w-sm"
              data-testid="forms-editor-title-label"
            >
              {title || t('builder.preview.titlePlaceholder')}
            </h1>
            {!isNew && form && <FormStatusBadge status={form.status} />}
          </div>

          <div className="flex items-center gap-2">
            {!isNew && (
              <Button
                variant="outline"
                onClick={() => setShareOpen(true)}
                data-testid="forms-editor-share-button"
              >
                <Share2 className="h-4 w-4 mr-2" />
                {t('list.rowActions.share')}
              </Button>
            )}
            <FormSettingsPopover
              emailRequired={emailRequired}
              onEmailRequiredChange={setEmailRequired}
              disabled={!isDraft}
              projectId={projectId}
              onProjectIdChange={setProjectId}
              scopeEditable={isNew}
            />
            {isDraft && (
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={isSaving || !title.trim()}
                data-testid="forms-builder-save-button"
              >
                {t('builder.saveButton')}
              </Button>
            )}
          </div>
        </div>

        <Tabs defaultValue={initialTab}>
          <TabsList>
            <TabsTrigger value="editor" data-testid="forms-editor-tab-editor">
              {t('builder.fieldsTitle')}
            </TabsTrigger>
            <TabsTrigger
              value="submissions"
              disabled={isNew}
              data-testid="forms-editor-tab-submissions"
            >
              {t('submissions.title')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="editor" className="mt-4">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-[380px_1fr]">
              <FormFieldsEditor
                fields={builderFields}
                onChange={setBuilderFields}
                readOnly={!isDraft}
              />
              <FormPreviewPane
                title={title}
                description={description}
                fields={builderFields}
                emailRequired={emailRequired}
                onTitleChange={setTitle}
                onDescriptionChange={setDescription}
                readOnly={!isDraft}
              />
            </div>
          </TabsContent>

          {!isNew && form && (
            <TabsContent value="submissions" className="mt-4 space-y-4">
              <FormAnalyticsDashboard
                formId={form.id}
                formCreatedAt={form.createdAt}
                formFields={form.fields}
              />
              <SubmissionsTable
                formId={form.id}
                fields={form.fields}
                submissions={submissions}
                isLoading={submissionsLoading}
                canCreateTask={!!form.projectId}
              />
              {totalSubmissions > 0 && <Pagination {...pagination} />}
            </TabsContent>
          )}
        </Tabs>

        {!isNew && form && (
          <ShareFormDialog
            formId={form.id}
            projectId={form.projectId}
            open={shareOpen}
            onOpenChange={setShareOpen}
          />
        )}
      </div>
    </FeatureErrorBoundary>
  );
}
